/**
 * Main application entry point
 * Imports from lib modules and initializes the application
 */

import {
  // Theme
  THEME_KEY,
  applyTheme,
  getCurrentTheme,
  toggleTheme,
  detectInitialTheme,
  // Mobile menu
  HAMBURGER_ICON,
  CLOSE_ICON,
  toggleMobileMenu,
  closeMobileMenu,
  openMobileMenu,
  isMobileMenuOpen,
  // i18n
  getLangFromUrl,
  updateUrlLang,
  isInternalUrl,
  rewriteInternalLinksForLang,
  detectInitialLang,
  setLangUI,
  // Scroll
  smoothScrollTo,
  scrollToTop,
  updateScrollProgress,
  addStaggerAnimation,
  // FAQ
  toggleFAQ,
  // Projects
  showProjectTab,
  isHydropowerProject,
  // Analytics
  initAnalytics,
} from './lib/index.mjs';

// Re-export for testing (will be tree-shaken in production bundle)
export {
  THEME_KEY,
  applyTheme,
  getCurrentTheme,
  toggleTheme,
  detectInitialTheme,
  HAMBURGER_ICON,
  CLOSE_ICON,
  toggleMobileMenu,
  closeMobileMenu,
  openMobileMenu,
  isMobileMenuOpen,
  getLangFromUrl,
  updateUrlLang,
  isInternalUrl,
  rewriteInternalLinksForLang,
  detectInitialLang,
  setLangUI,
  smoothScrollTo,
  scrollToTop,
  updateScrollProgress,
  addStaggerAnimation,
  toggleFAQ,
  showProjectTab,
  isHydropowerProject,
};

(() => {
  // i18n translations
  const i18n = {
    en: {
      'brand.name': 'Chareun & Associates Co., Ltd. (CaA)',
      'nav.services': 'Services',
      'nav.about': 'About',
      'nav.expertise': 'Our Expertise',
      'nav.projects': 'Projects',
      'nav.team': 'Team',
      'nav.contact': 'Contact',
      'nav.language': 'Language',
      'about.why_choose': 'Why Choose CaA for Environmental Consulting in Laos?',
      'about.monre_accredited':
        'MONRE Accredited: Officially recognized by the Ministry of Natural Resources and Environment',
      'about.experience':
        '20+ Years Experience: Deep expertise in Laos environmental regulations and standards',
      'about.international':
        'International Standards: Compliance with World Bank, ADB, and JICA requirements',
      'about.local': 'Local Expertise: Bilingual team fluent in English and Lao languages',
      'about.track_record':
        'Proven Track Record: Successfully completed 100+ ESIA projects across Laos',
      'about.eia_services': 'Environmental Impact Assessment Services',
      'about.hydro_esia': 'Hydropower ESIA: Comprehensive assessments for dam and power projects',
      'about.infrastructure_esia':
        'Infrastructure ESIA: Roads, bridges, and transportation projects',
      'about.mining_esia': 'Mining ESIA: Environmental studies for extraction projects',
      'about.industrial_esia': 'Industrial ESIA: Manufacturing and processing facilities',
      'about.baseline': 'Baseline Studies: Environmental and social baseline data collection',
      'about.social_services': 'Social Impact Assessment & Community Development',
      'about.rap': 'Resettlement Action Plans (RAP): Comprehensive relocation planning',
      'about.community': 'Community Engagement: Stakeholder consultation and participation',
      'about.socio_economic': 'Socio-economic Studies: Impact assessment on local communities',
      'about.livelihood': 'Livelihood Restoration: Sustainable income generation programs',
      'about.cultural': 'Cultural Heritage: Protection of cultural and historical sites',
      'faq.title': 'Frequently Asked Questions - Environmental Consulting in Laos',
      'faq.section': 'Common Questions About ESIA in Laos',
      'faq.what_esia': 'What is an Environmental and Social Impact Assessment (ESIA)?',
      'faq.what_esia_answer':
        'An ESIA is a comprehensive study that evaluates the potential environmental and social impacts of a proposed project. In Laos, ESIA studies are mandatory for large-scale development projects including hydropower, mining, and infrastructure developments. Our team conducts thorough assessments following international standards and local regulations.',
      'faq.how_long': 'How long does an ESIA study take in Laos?',
      'faq.how_long_answer':
        'The duration of an ESIA study varies depending on project complexity and scope. Typically, our ESIA studies take 6-12 months to complete, including baseline data collection, impact assessment, stakeholder consultation, and report preparation. We ensure timely delivery while maintaining the highest quality standards.',
      'faq.requirements': 'What are the requirements for ESIA approval in Laos?',
      'faq.requirements_answer':
        'ESIA studies in Laos must comply with MONRE (Ministry of Natural Resources and Environment) guidelines and international standards. Our team ensures compliance with World Bank, ADB, and JICA requirements. We handle the entire approval process, from initial submission to final approval.',
      'faq.resettlement': 'Do you provide resettlement planning services?',
      'faq.resettlement_answer':
        'Yes, we specialize in Resettlement Action Plans (RAP) and livelihood restoration programs. Our team has extensive experience in community engagement, socio-economic studies, and ensuring affected communities receive fair compensation and sustainable livelihood opportunities.',
      'faq.sectors': 'What sectors do you serve in Laos?',
      'faq.sectors_answer':
        'We serve multiple sectors including hydropower, mining, infrastructure, transportation, and industrial development. Our expertise covers environmental impact assessment, social impact studies, waste management, and rural development projects across all provinces of Laos.',
      'faq.certified': 'Are your consultants certified and accredited?',
      'faq.certified_answer':
        'Yes, CaA is officially accredited by MONRE and our team includes certified environmental and social specialists. Our Managing Director and Senior Advisor have over 20 years of international experience and are fluent in both English and Lao languages.',
      'service_areas.title': 'Environmental Consulting Services Across Laos',
      'service_areas.vientiane': 'Vientiane Capital',
      'service_areas.vientiane_desc': 'Headquarters and main office serving central Laos',
      'service_areas.northern': 'Northern Laos',
      'service_areas.northern_desc': 'Hydropower and mining projects in Luang Prabang, Oudomxay',
      'service_areas.central': 'Central Laos',
      'service_areas.central_desc': 'Infrastructure and industrial development projects',
      'service_areas.southern': 'Southern Laos',
      'service_areas.southern_desc':
        'Mining and agricultural development in Champasak, Savannakhet',
      'hero.headline': 'Environmental & Social Impact Assessment Consulting in Laos',
      'hero.sub': 'Elevating Your Projects with Sustainable Expertise.',
      'hero.cta': 'Contact Us Today.',
      'hero.explore_services': 'Explore Services',
      // Kickers
      'kicker.monre_since': 'MONRE Accredited Since 2005',
      'kicker.what_we_do': 'What We Do',
      'kicker.our_work': 'Our Work',
      'about.title': 'About Us',
      'about.h1': 'About Chareun & Associates (CaA)',
      'about.body':
        'At Chareun & Associates Co., Ltd. (CaA), we are a professional services and project management company in Laos PDR, specializing in Environmental and Social Impact Assessments (ESIA). With nearly two decades of experience, we provide expert guidance to ensure your projects are not only successful but also socially and environmentally responsible. Our deep understanding of local cultural and political governance issues is essential for effective project delivery.',
      'expertise.title': 'Our Expertise',
      'expertise.intro':
        'CaA is accredited by the Ministry of Natural Resources and Environment (MONRE). Our core areas of specialization include:',
      'expertise.esia.title': 'Environmental and Social Impact Assessments (ESIA)',
      'expertise.esia.body':
        'We provide comprehensive studies for both hydropower and non-hydropower projects, ensuring compliance with international and national standards.',
      'expertise.socio.title': 'Socio-Economic Studies & Resettlement',
      'expertise.socio.body':
        'Our team is highly experienced in conducting socio-economic studies and developing Resettlement Action Plans (RAP) for affected communities.',
      'expertise.rural.title': 'Rural Development & Community Engagement',
      'expertise.rural.body':
        'We work closely with local government departments, universities, and communities to implement sustainable development programs and strengthen local organizations.',
      'expertise.waste.title': 'Waste Management',
      'expertise.waste.body':
        'Our expertise extends to the management and treatment of domestic and industrial waste, including the design of systems like wastewater treatment and wetlands.',
      'projects.title': 'Key Projects & Experience',
      'projects.intro':
        'We have a proven track record of successful projects with major international and private sector clients. Our work spans a wide range of sectors, including:',
      'projects.hydro.title': 'Hydropower',
      'projects.hydro.body':
        'We have served as the Independent Monitoring Agency (IMA) for the Nam Ngiep 1 Hydro Power Project since 2016, carrying out socio-environmental monitoring on behalf of the Ministry Of Natural Resource & Environment.',
      'projects.transport.title': 'Transport & Infrastructure',
      'projects.transport.body':
        'We have conducted environmental and social studies for major projects such as the Boten-Houaxay Expressway and National Road No. 6.',
      'projects.mining.title': 'Industrial & Mining',
      'projects.mining.body':
        'We have completed an Environmental Impact Assessment (EIA) for a Copper Smelting Factory and updated Environmental Management and Monitoring Plans (EMMP) for the Southern Mining Smelting Sole company.',
      'projects.clients': 'Clients',
      // Added missing keys used on homepage
      'clients.title': 'Our Clients',
      'featured.title': 'Featured Projects',
      'gallery.title': 'Project Gallery',
      'certificates.title': 'Certificates & Accreditation',
      'services.esia.title': 'Environmental & Social Impact Assessments (ESIA)',
      'services.esia.body': 'Comprehensive assessments for hydropower and infrastructure projects',
      'services.socio.title': 'Socio-Economic Studies & Resettlement',
      'services.socio.body': 'Expert resettlement planning and community engagement',
      'services.rural.title': 'Rural Development Projects',
      'services.rural.body': 'Sustainable development programs and community strengthening',
      'services.waste.title': 'Waste Management for Industrial Projects',
      'services.waste.body': 'Industrial waste treatment and environmental compliance',
      'services.title': 'Our Services',
      'services.intro':
        'We provide comprehensive environmental and social consulting services across Laos, specializing in ESIA, resettlement planning, and sustainable development.',
      'services.learn_more': 'Learn More About Our Services',
      'featured.intro':
        'With over 20 years of experience, we have successfully completed 100+ environmental and social impact assessment projects across Laos.',
      'featured.view_all': 'View All Projects',
      'featured.project1.title': 'Nam Ngiep 1 Hydro',
      'featured.project1.client': 'Nam Ngiep 1 Power Company',
      'featured.project1.year': '2016-2024',
      'featured.project1.description':
        'Independent Monitoring Agency (IMA) for socio-environmental monitoring on behalf of MONRE.',
      'featured.project2.title': 'Solar Power Project 100MW',
      'featured.project2.client': 'Private Developer',
      'featured.project2.year': '2022',
      'featured.project2.description':
        'Environmental and Social Impact Assessment for large-scale solar energy development.',
      'featured.project3.title': 'Phou-Tevada Tourist Development',
      'featured.project3.client': 'Tourism Development Company',
      'featured.project3.year': '2020-2021',
      'featured.project3.description':
        'Comprehensive ESIA and community engagement for sustainable tourism development.',
      // Taxonomy/tag labels
      'tag.hydropower': 'Hydropower',
      'tag.renewable': 'Renewable Energy',
      'tag.tourism': 'Tourism',
      'label.client': 'Client:',
      'label.year': 'Year:',
      'label.funds': 'Funds:',
      'label.location': 'Location:',
      'label.duration': 'Duration:',
      'label.tasks': 'Tasks:',
      'label.status': 'Status:',
      'cta.title': 'Contact us today to discuss your project.',
      'cta.sub':
        'Let our experienced team help you navigate environmental and social requirements for your development project in Laos.',
      'cta.button': 'Get Started Today',
      'cta.view_contact': 'View Contact Info',
      'team.title': 'Our Team',
      'team.lead1.title': 'Mr. Chareun Sayakoummane - Managing Director',
      'team.lead1.body':
        'An Australian citizen fluent in English and Lao, he has over twenty years of experience in Australia and more than fifteen years in Laos PDR, focusing on environmental and social impact studies and project management.',
      'team.lead2.title': 'Mr. Les Taylor - General Senior Advisor',
      'team.lead2.body':
        'An Australian citizen with over 20 years of international experience, he has lived and worked in Laos and Southeast Asia for the past 15 years. He is fluent in Lao and Thai and has extensive experience in environmental and social impact assessment and waste treatment.',
      'team.desc':
        'Our permanent staff includes a diverse team of qualified Lao professionals, with specialists in rural development, forestry, economics, and engineering.',
      'contact.title': 'Contact Us',
      'contact.address_label': 'Address',
      'contact.email_label': 'Email',
      'contact.phone_label': 'Phone',
      'footer.copy': '© {year} Chareun & Associates Co., Ltd. All rights reserved.',
      'footer.tech': 'Made with Tailwind CSS. Hosted on Vercel/Cloudflare Pages.',
    },
    lo: {
      // NOTE: Placeholder Lao strings; replace with approved Lao translations
      'brand.name': 'ຊາຣືນ & ບໍລິສັດ ຈໍາກັດ (CaA)',
      'nav.services': 'ບໍລິການ',
      'nav.about': 'ກ່ຽວກັບ',
      'nav.expertise': 'ຄວາມຊໍານານ',
      'nav.projects': 'ໂຄງການ',
      'nav.team': 'ທີມງານ',
      'nav.contact': 'ຕິດຕໍ່',
      'nav.language': 'ພາສາ',
      'about.why_choose': 'ເປັນຫຍັງຕ້ອງເລືອກ CaA ສໍາລັບການທີ່ປຶກສາສິ່ງແວດລ້ອມໃນລາວ?',
      'about.monre_accredited':
        'ຮັບຮອງ MONRE: ຮັບຮອງຢ່າງເປັນທາງການໂດຍກະຊວງຊັບພະຍາກອນທໍາມະຊາດ ແລະ ສິ່ງແວດລ້ອມ',
      'about.experience': 'ປະສົບການ 20+ ປີ: ຄວາມຊໍານານເລິກໃນກົດລະບຽບ ແລະ ມາດຕະຖານສິ່ງແວດລ້ອມລາວ',
      'about.international': 'ມາດຕະຖານສາກົນ: ສອດຄ່ອງກັບຄວາມຕ້ອງການຂອງ World Bank, ADB, ແລະ JICA',
      'about.local': 'ຄວາມຊໍານານທ້ອງຖິ່ນ: ທີມງານສອງພາສາທີ່ເວົ້າພາສາອັງກິດ ແລະ ລາວໄດ້',
      'about.track_record': 'ປະຫວັດການເຮັດວຽກທີ່ພິສູດແລ້ວ: ສໍາເລັດໂຄງການ ESIA 100+ ໂຄງການທົ່ວລາວ',
      'about.eia_services': 'ບໍລິການປະເມີນຜົນກະທົບສິ່ງແວດລ້ອມ',
      'about.hydro_esia': 'ESIA ໄຟຟ້ານ້ໍາຕົກ: ການປະເມີນທີ່ຄົບວົງຈອນສໍາລັບໂຄງການເຂື່ອນ ແລະ ໄຟຟ້າ',
      'about.infrastructure_esia': 'ESIA ໂຄງສ້າງພື້ນຖານ: ໂຄງການຖະໜົນ, ຂົວ, ແລະ ການຂົນສົ່ງ',
      'about.mining_esia': 'ESIA ການຂຸດຄົ້ນ: ການສຶກສາສິ່ງແວດລ້ອມສໍາລັບໂຄງການຂຸດຄົ້ນ',
      'about.industrial_esia': 'ESIA ອຸດສາຫະກໍາ: ໂຮງງານຜະລິດ ແລະ ປຸງແຕ່ງ',
      'about.baseline': 'ການສຶກສາພື້ນຖານ: ການເກັບກໍາຂໍ້ມູນພື້ນຖານສິ່ງແວດລ້ອມ ແລະ ສັງຄົມ',
      'about.social_services': 'ການປະເມີນຜົນກະທົບສັງຄົມ & ພັດທະນາຊຸມຊົນ',
      'about.rap': 'ແຜນການອົບຍ້າຍ (RAP): ການວາງແຜນຍ້າຍທີ່ຄົບວົງຈອນ',
      'about.community': 'ການມີສ່ວນຮ່ວມຊຸມຊົນ: ການປຶກສາຫາລື ແລະ ການມີສ່ວນຮ່ວມຂອງພາກສ່ວນທີ່ກ່ຽວຂ້ອງ',
      'about.socio_economic': 'ການສຶກສາສັງຄົມ-ເສດຖະກິດ: ການປະເມີນຜົນກະທົບຕໍ່ຊຸມຊົນທ້ອງຖິ່ນ',
      'about.livelihood': 'ການຟື້ນຟູການດໍາລົງຊີວິດ: ໂຄງການສ້າງລາຍໄດ້ທີ່ຍືນຍົງ',
      'about.cultural': 'ມໍລະດົກວັດທະນະທໍາ: ການປົກປ້ອງສະຖານທີ່ວັດທະນະທໍາ ແລະ ປະຫວັດສາດ',
      'faq.title': 'ຄໍາຖາມທີ່ຖາມເລື້ອຍ - ການທີ່ປຶກສາສິ່ງແວດລ້ອມໃນລາວ',
      'faq.what_esia': 'ການປະເມີນຜົນກະທົບສິ່ງແວດລ້ອມ ແລະ ສັງຄົມ (ESIA) ແມ່ນຫຍັງ?',
      'faq.what_esia_answer':
        'ESIA ແມ່ນການສຶກສາທີ່ຄົບວົງຈອນທີ່ປະເມີນຜົນກະທົບສິ່ງແວດລ້ອມ ແລະ ສັງຄົມທີ່ອາດເກີດຂຶ້ນຂອງໂຄງການທີ່ສະເໜີ. ໃນລາວ, ການສຶກສາ ESIA ແມ່ນບັງຄັບສໍາລັບໂຄງການພັດທະນາຂະໜາດໃຫຍ່ລວມທັງໄຟຟ້ານ້ໍາຕົກ, ການຂຸດຄົ້ນ, ແລະ ໂຄງສ້າງພື້ນຖານ. ທີມງານຂອງພວກເຮົາດໍາເນີນການປະເມີນຢ່າງລະມັດລະວັງຕາມມາດຕະຖານສາກົນ ແລະ ກົດລະບຽບທ້ອງຖິ່ນ.',
      'faq.how_long': 'ການສຶກສາ ESIA ໃນລາວໃຊ້ເວລາເທົ່າໃດ?',
      'faq.how_long_answer':
        'ການສຶກສາ ESIA ແມ່ນຂຶ້ນກັບຄວາມຊັບຊ້ອນ ແລະ ຂອບເຂດຂອງໂຄງການ. ໂດຍທົ່ວໄປ, ການສຶກສາ ESIA ຂອງພວກເຮົາໃຊ້ເວລາ 6-12 ເດືອນເພື່ອສໍາເລັດ, ລວມທັງການເກັບກໍາຂໍ້ມູນພື້ນຖານ, ການປະເມີນຜົນກະທົບ, ການປຶກສາຫາລືກັບພາກສ່ວນທີ່ກ່ຽວຂ້ອງ, ແລະ ການກະກຽມລາຍງານ. ພວກເຮົາຮັບປະກັນການສົ່ງມອບທີ່ທັນເວລາໃນຂະນະທີ່ຮັກສາມາດຕະຖານຄຸນນະພາບສູງສຸດ.',
      'faq.requirements': 'ຄວາມຕ້ອງການສໍາລັບການອະນຸມັດ ESIA ໃນລາວແມ່ນຫຍັງ?',
      'faq.requirements_answer':
        'ການສຶກສາ ESIA ໃນລາວຕ້ອງສອດຄ່ອງກັບແນວທາງຂອງ MONRE (ກະຊວງຊັບພະຍາກອນທໍາມະຊາດ ແລະ ສິ່ງແວດລ້ອມ) ແລະ ມາດຕະຖານສາກົນ. ທີມງານຂອງພວກເຮົາຮັບປະກັນການສອດຄ່ອງກັບຄວາມຕ້ອງການຂອງ World Bank, ADB, ແລະ JICA. ພວກເຮົາຈັດການຂະບວນການອະນຸມັດທັງໝົດ, ຈາກການສົ່ງເບື້ອງຕົ້ນຈົນເຖິງການອະນຸມັດສຸດທ້າຍ.',
      'faq.resettlement': 'ທ່ານໃຫ້ບໍລິການວາງແຜນການອົບຍ້າຍບໍ?',
      'faq.resettlement_answer':
        'ແມ່ນແລ້ວ, ພວກເຮົາເຊື່ອງໃນແຜນການອົບຍ້າຍ (RAP) ແລະ ໂຄງການຟື້ນຟູການດໍາລົງຊີວິດ. ທີມງານຂອງພວກເຮົາມີປະສົບການກວ່າງໃນການມີສ່ວນຮ່ວມຊຸມຊົນ, ການສຶກສາສັງຄົມ-ເສດຖະກິດ, ແລະ ຮັບປະກັນວ່າຊຸມຊົນທີ່ໄດ້ຮັບຜົນກະທົບໄດ້ຮັບການຊົດເຊີຍທີ່ຍຸດຕິທໍາ ແລະ ໂອກາດການດໍາລົງຊີວິດທີ່ຍືນຍົງ.',
      'faq.sectors': 'ທ່ານໃຫ້ບໍລິການໃນພາກສ່ວນໃດໃນລາວ?',
      'faq.sectors_answer':
        'ພວກເຮົາໃຫ້ບໍລິການໃນຫຼາຍພາກສ່ວນລວມທັງໄຟຟ້ານ້ໍາຕົກ, ການຂຸດຄົ້ນ, ໂຄງສ້າງພື້ນຖານ, ການຂົນສົ່ງ, ແລະ ການພັດທະນາອຸດສາຫະກໍາ. ຄວາມຊໍານານຂອງພວກເຮົາຄອບຄຸມການປະເມີນຜົນກະທົບສິ່ງແວດລ້ອມ, ການສຶກສາຜົນກະທົບສັງຄົມ, ການຈັດການຂີ້ເຫຼືອ, ແລະ ໂຄງການພັດທະນາຊົນນະບົດທົ່ວທຸກແຂວງຂອງລາວ.',
      'faq.certified': 'ທີ່ປຶກສາຂອງທ່ານມີໃບຢັ້ງຢືນ ແລະ ຮັບຮອງບໍ?',
      'faq.certified_answer':
        'ແມ່ນແລ້ວ, CaA ໄດ້ຮັບຮອງຢ່າງເປັນທາງການໂດຍ MONRE ແລະ ທີມງານຂອງພວກເຮົາລວມມີຜູ້ຊໍານານດ້ານສິ່ງແວດລ້ອມ ແລະ ສັງຄົມ. ຜູ້ອໍານວຍການ ແລະ ທີ່ປຶກສາອາວຸໂສຂອງພວກເຮົາມີປະສົບການສາກົນກວ່າ 20 ປີ ແລະ ເວົ້າພາສາອັງກິດ ແລະ ລາວໄດ້.',
      'service_areas.title': 'ບໍລິການທີ່ປຶກສາສິ່ງແວດລ້ອມທົ່ວລາວ',
      'service_areas.vientiane': 'ນະຄອນຫຼວງວຽງຈັນ',
      'service_areas.vientiane_desc': 'ສໍານັກງານໃຫຍ່ ແລະ ສໍານັກງານຫຼັກທີ່ໃຫ້ບໍລິການລາວກາງ',
      'service_areas.northern': 'ລາວເໜືອ',
      'service_areas.northern_desc': 'ໂຄງການໄຟຟ້ານ້ໍາຕົກ ແລະ ການຂຸດຄົ້ນໃນຫຼວງພະບາງ, ອຸດົມໄຊ',
      'service_areas.central': 'ລາວກາງ',
      'service_areas.central_desc': 'ໂຄງການໂຄງສ້າງພື້ນຖານ ແລະ ພັດທະນາອຸດສາຫະກໍາ',
      'service_areas.southern': 'ລາວໃຕ້',
      'service_areas.southern_desc': 'ການຂຸດຄົ້ນ ແລະ ພັດທະນາກະສິກໍາໃນຈໍາປາສັກ, ສະຫວັນນະເຂດ',
      'hero.headline':
        'Chareun & Associates Co., Ltd. (CaA) - ທີ່ປຶກສາດ້ານສິ່ງແວດລ້ອມ & ສັງຄົມ ໃນລາວ.',
      'hero.sub': 'ການທີ່ປຶກສາດ້ານສິ່ງແວດລ້ອມ & ສັງຄົມໃນລາວ ຕັ້ງແຕ່ປີ 2005',
      'hero.cta': 'ຕິດຕໍ່ເຮົາມື້ນີ້.',
      'hero.explore_services': 'ສໍາຫຼວດບໍລິການ',
      // Kickers
      'kicker.monre_since': 'ຮັບຮອງ MONRE ຕັ້ງແຕ່ 2005',
      'kicker.what_we_do': 'ພວກເຮົາເຮັດຫຍັງ',
      'kicker.our_work': 'ຜົນງານຂອງພວກເຮົາ',
      'about.title': 'ກ່ຽວກັບພວກເຮົາ',
      'about.body':
        'ບໍລິສັດ Chareun & Associates Co., Ltd. (CaA) ເປັນບໍລິສັດບໍລິການວິຊາຊີບ ແລະ ບໍລິຫານໂຄງການ ໃນ ສປປ ລາວ ເຊິ່ງເຊື່ອງໃນການປະເມີນຜົນກະທົບດ້ານສິ່ງແວດລ້ອມ ແລະ ສັງຄົມ (ESIA). ດ້ວຍປະສົບການເກືອບສອງທົດສະວັດ, ພວກເຮົາໃຫ້ຄໍາແນະນໍາຊ່ຽວຊານເພື່ອໃຫ້ໂຄງການຂອງທ່ານປະສົບຄວາມສໍາເລັດ ແລະ ຮັບຜິດຊອບຕໍ່ສັງຄົມ-ສິ່ງແວດລ້ອມ. ຄວາມເຂົ້າໃຈລັກສະນະວັດທະນະທໍາ ແລະ ການບໍລິຫານການເມືອງທ້ອງຖິ່ນ ເປັນກະລຸນາສໍາຄັນສໍາລັບການດໍາເນີນໂຄງການ.',
      'expertise.title': 'ຄວາມຊໍານານຂອງພວກເຮົາ',
      'expertise.intro':
        'CaA ໄດ້ຮັບຮອງຈາກ ກະຊວງ ຊພພ ແລະ ສິ່ງແວດລ້ອມ (MONRE). ຂອບເຂດຫຼັກຂອງພວກເຮົາປະກອບມີ:',
      'expertise.esia.title': 'ESIA – ການປະເມີນຜົນກະທົບສິ່ງແວດລ້ອມ ແລະ ສັງຄົມ',
      'expertise.esia.body':
        'ສຶກສາຄົບວົງຈອນສໍາລັບໂຄງການໄຟຟ້ານ້ໍາຕົກ ແລະ ບໍ່ແມ່ນໄຟຟ້ານ້ໍາຕົກ ໃຫ້ສອດຄ່ອງມາດຕະຖານສາກົນ ແລະ ແຫ່ງຊາດ.',
      'expertise.socio.title': 'ສັງຄົມ-ເສດຖະກິດ & ການອົບຍ້າຍ',
      'expertise.socio.body':
        'ຊໍານານໃນການສໍາຫຼວດສັງຄົມ-ເສດຖະກິດ ແລະ ຈັດທໍາແຜນອົບຍ້າຍ (RAP) ສໍາລັບຊຸມຊົນທີ່ໄດ້ຮັບຜົນກະທົບ.',
      'expertise.rural.title': 'ພັດທະນາຊົນນະບົດ & ມີສ່ວນຮ່ວມຊຸມຊົນ',
      'expertise.rural.body':
        'ຮ່ວມມືກັບພາກສ່ວນລັດ, ໂຮງຮຽນ ແລະ ຊຸມຊົນ ໃນການປະຕິບັດໂຄງການພັດທະນາທີ່ຍືນຍົງ.',
      'expertise.waste.title': 'ການຈັດການຂີ້ເຫຼືອ',
      'expertise.waste.body':
        'ຄຸ້ມຄອງຂີ້ເຫຼືອພາຍໃນເຮືອນເຮືອນ ແລະ ອຸດສາຫະກໍາ ຮວມທັງອອກແບບລະບົບບໍາບັດນ້ໍາເສຍ ແລະ ພື້ນທີ່ຊຸມນ້ໍາ (wetlands).',
      'projects.title': 'ໂຄງການ & ປະສົບການສໍາຄັນ',
      'projects.intro': 'ມີປະຫວັດການເຮັດວຽກສໍາເລັດກັບລູກຄ້າສາກົນ ແລະ ເອກະຊົນ ຫຼາຍພາກສ່ວນ:',
      'projects.hydro.title': 'ໄຟຟ້ານ້ໍາຕົກ',
      'projects.hydro.body':
        'ເຮັດໜ້າທີ່ IMA ສໍາລັບໂຄງການ ນ້ໍາງີບ 1 ຕັ້ງແຕ່ 2016 ເຮັດການຕິດຕາມສິ່ງແວດລ້ອມ-ສັງຄົມ ໃຫ້ MONRE.',
      'projects.transport.title': 'ຂົນສົ່ງ & ໂຄງສ້າງພື້ນຖານ',
      'projects.transport.body':
        'ສຶກສາດ້ານສິ່ງແວດລ້ອມ-ສັງຄົມ ສໍາລັບໂຄງການ ມໍເຕີເວຍ ໂບເຕນ-ຮ່ວຍໄຊ ແລະ ທາງໝາຍເລກ 6.',
      'projects.mining.title': 'ອຸດສາຫະກໍາ & ການຂຸດຄົ້ນ',
      'projects.mining.body':
        'EIA ໂຮງຫຼໍ່ທອງແດງ ແລະ ປັບປຸງ EMMP ສໍາລັບ Southern Mining Smelting Sole.',
      'projects.clients': 'ລູກຄ້າ',
      'team.title': 'ທີມງານຂອງພວກເຮົາ',
      'team.lead1.title': 'ທ່ານ ຊາຣືນ ໄຊະຄຸມມານ - ຜູ້ອໍານວຍການ',
      'team.lead1.body':
        'ສັນຊາດອົດສະຕາລີ ເວົ້າພາສາອັງກິດ ແລະ ລາວ ປະສົບການ 20+ ປີ ໃນອົດສະຕາລີ ແລະ 15+ ປີ ໃນ ສປປ ລາວ.',
      'team.lead2.title': 'ທ່ານ ເລັດ ເທລເລີ - ທີ່ປຶກສາອາວຸໂສ',
      'team.lead2.body': 'ສັນຊາດອົດສະຕາລີ ປະສົບການສາກົນ 20+ ປີ ເຮັດວຽກໃນ ສປປ ລາວ ແລະ ອາຊຽນ 15+ ປີ.',
      'team.desc':
        'ພະນັກງານປະຈໍາຂອງພວກເຮົາມີຄວາມຫຼາກຫຼາຍ ພ້ອມຜູ້ຊໍານານດ້ານຊົນນະບົດ, ປ່າໄມ້, ເສດຖະກິດ, ແລະ ວິສະວະກໍາ.',
      'contact.title': 'ຕິດຕໍ່ເຮົາ',
      'contact.address_label': 'ທີ່ຢູ່',
      'contact.email_label': 'ອີເມວ',
      'contact.phone_label': 'ໂທລະສັບ',
      'cta.view_contact': 'ເບິ່ງຂໍ້ມູນຕິດຕໍ່',
      'services.title': 'ບໍລິການຂອງພວກເຮົາ',
      'services.intro':
        'ພວກເຮົາໃຫ້ບໍລິການທີ່ປຶກສາດ້ານສິ່ງແວດລ້ອມ ແລະ ສັງຄົມທີ່ຄົບວົງຈອນທົ່ວລາວ, ເຊື່ອງໃນ ESIA, ການວາງແຜນອົບຍ້າຍ, ແລະ ການພັດທະນາທີ່ຍືນຍົງ.',
      'services.esia.title': 'ການປະເມີນຜົນກະທົບສິ່ງແວດລ້ອມ ແລະ ສັງຄົມ (ESIA)',
      'services.esia.body': 'ການປະເມີນທີ່ຄົບວົງຈອນສໍາລັບໂຄງການໄຟຟ້ານ້ໍາຕົກ ແລະ ໂຄງສ້າງພື້ນຖານ',
      'services.socio.title': 'ການສຶກສາສັງຄົມ-ເສດຖະກິດ ແລະ ການອົບຍ້າຍ',
      'services.socio.body': 'ການວາງແຜນອົບຍ້າຍທີ່ຊໍານານ ແລະ ການມີສ່ວນຮ່ວມຊຸມຊົນ',
      'services.rural.title': 'ໂຄງການພັດທະນາຊົນນະບົດ',
      'services.rural.body': 'ໂຄງການພັດທະນາທີ່ຍືນຍົງ ແລະ ການເສີມສ້າງຊຸມຊົນ',
      'services.waste.title': 'ການຈັດການຂີ້ເຫຼືອສໍາລັບໂຄງການອຸດສາຫະກໍາ',
      'services.waste.body': 'ການປຸງແຕ່ງຂີ້ເຫຼືອອຸດສາຫະກໍາ ແລະ ການປະຕິບັດຕາມມາດຕະຖານສິ່ງແວດລ້ອມ',
      'services.learn_more': 'ຮຽນຮູ້ເພີ່ມເຕີມກ່ຽວກັບບໍລິການຂອງພວກເຮົາ',
      'featured.title': 'ໂຄງການໂດດເດັ່ນ',
      'featured.intro':
        'ດ້ວຍປະສົບການກວ່າ 20 ປີ, ພວກເຮົາໄດ້ສໍາເລັດໂຄງການປະເມີນຜົນກະທົບສິ່ງແວດລ້ອມ ແລະ ສັງຄົມ 100+ ໂຄງການທົ່ວລາວ.',
      'featured.view_all': 'ເບິ່ງໂຄງການທັງໝົດ',
      'featured.project1.title': 'Nam Ngiep 1 Hydro',
      'featured.project1.client': 'Nam Ngiep 1 Power Company',
      'featured.project1.year': '2016-2024',
      'featured.project1.description':
        'ອົງການຕິດຕາມອິດສະຫຼະ (IMA) ສໍາລັບການຕິດຕາມສິ່ງແວດລ້ອມ-ສັງຄົມໃນນາມ MONRE.',
      'featured.project2.title': 'ໂຄງການແສງຕາເວັນ 100MW',
      'featured.project2.client': 'ຜູ້ພັດທະນາສ່ວນຕົວ',
      'featured.project2.year': '2022',
      'featured.project2.description':
        'ການປະເມີນຜົນກະທົບສິ່ງແວດລ້ອມ ແລະ ສັງຄົມສໍາລັບການພັດທະນາພະລັງງານແສງຕາເວັນຂະໜາດໃຫຍ່.',
      'featured.project3.title': 'ການພັດທະນາທ່ອງທ່ຽວ Phou-Tevada',
      'featured.project3.client': 'ບໍລິສັດພັດທະນາທ່ອງທ່ຽວ',
      'featured.project3.year': '2020-2021',
      'featured.project3.description':
        'ESIA ທີ່ຄົບວົງຈອນ ແລະ ການມີສ່ວນຮ່ວມຊຸມຊົນສໍາລັບການພັດທະນາທ່ອງທ່ຽວທີ່ຍືນຍົງ.',
      // Taxonomy/tag labels (Lao)
      'tag.hydropower': 'ໄຟຟ້ານ້ໍາຕົກ',
      'tag.renewable': 'ພະລັງງານທົດແທນ',
      'tag.tourism': 'ທ່ອງທ່ຽວ',
      // Detail labels (Lao)
      'label.client': 'ລູກຄ້າ:',
      'label.year': 'ປີ:',
      'label.funds': 'ທຶນ:',
      'label.location': 'ທີ່ຕັ້ງ:',
      'label.duration': 'ໄລຍະເວລາ:',
      'label.tasks': 'ໜ້າທີ່:',
      'label.status': 'ສະຖານະ:',
      'footer.copy': '© {year} Chareun & Associates Co., Ltd. ສິດທິທັງໝົດຖືກສົງວນ.',
      'footer.tech': 'ສ້າງດ້ວຍ Tailwind CSS. ໂຮສຕ໌ຢູ່ Vercel/Cloudflare Pages.',
    },
  };

  // Initialize theme
  const initialTheme = detectInitialTheme();
  applyTheme(initialTheme);

  // Theme toggle handler
  document.addEventListener('click', (e) => {
    const target = e.target;
    if (!(target instanceof Element)) return;
    const btn = target.closest('[data-theme-toggle]');
    if (!btn) return;
    e.preventDefault();
    toggleTheme();
  });

  // Mobile menu handlers
  document.addEventListener('click', (e) => {
    const target = e.target instanceof Element ? e.target : null;
    if (!target) return;
    const menuBtn = document.getElementById('menuBtn');
    const mobileMenu = document.getElementById('mobileMenu');
    if (!menuBtn || !mobileMenu) return;

    if (menuBtn.contains(target)) {
      e.preventDefault();
      e.stopPropagation();
      toggleMobileMenu(menuBtn, mobileMenu);
      return;
    }
    if (target.closest('#mobileMenu a')) {
      closeMobileMenu(menuBtn, mobileMenu);
      return;
    }
    if (!menuBtn.contains(target) && !mobileMenu.contains(target) && isMobileMenuOpen(mobileMenu)) {
      closeMobileMenu(menuBtn, mobileMenu);
    }
  });

  document.addEventListener(
    'touchstart',
    (e) => {
      const target = e.target instanceof Element ? e.target : null;
      if (!target?.closest('#menuBtn')) return;
      const menuBtn = document.getElementById('menuBtn');
      const mobileMenu = document.getElementById('mobileMenu');
      if (menuBtn && mobileMenu) {
        e.preventDefault();
        toggleMobileMenu(menuBtn, mobileMenu);
      }
    },
    { passive: false }
  );

  document.addEventListener('keydown', (e) => {
    const mobileMenu = document.getElementById('mobileMenu');
    const menuBtn = document.getElementById('menuBtn');
    if (e.key === 'Escape' && mobileMenu && isMobileMenuOpen(mobileMenu)) {
      closeMobileMenu(menuBtn, mobileMenu);
      menuBtn?.focus();
    }
  });

  // Year in footer
  const yearEl = document.getElementById('year');
  if (yearEl) {
    yearEl.textContent = new Date().getFullYear();
  }

  /**
   * Applies the selected language to all i18n elements
   * @param {string} lang - Language code ('en' or 'lo')
   */
  function applyLanguage(lang) {
    const dict = i18n[lang] || i18n.en;
    document.documentElement.lang = lang;

    // Title, description and canonical stay as authored per page (SEO); only og:locale follows the UI language
    const ogLocale = document.querySelector('meta[property="og:locale"]');
    if (ogLocale) ogLocale.setAttribute('content', lang === 'lo' ? 'lo_LA' : 'en_US');

    document.querySelectorAll('[data-i18n]').forEach((el) => {
      const key = el.getAttribute('data-i18n');
      const value = dict && dict[key] !== undefined ? dict[key] : i18n.en && i18n.en[key];
      if (value === undefined) return;
      if (key === 'footer.copy') {
        el.innerHTML = value.replace('{year}', String(new Date().getFullYear()));
      } else {
        el.textContent = value;
      }
    });

    const allToggles = document.querySelectorAll('button[data-lang]');
    allToggles.forEach((btn) => {
      const isActive = btn.getAttribute('data-lang') === lang;
      btn.setAttribute('aria-pressed', String(isActive));
      btn.classList.toggle('bg-primary', isActive);
      btn.classList.toggle('text-white', isActive);
    });
  }

  // Initialize language
  const urlLang = getLangFromUrl();
  const storedLang = localStorage.getItem('lang');
  const initialLang = urlLang || storedLang || detectInitialLang();
  applyLanguage(initialLang);
  localStorage.setItem('lang', initialLang);
  setLangUI(initialLang);
  updateUrlLang(initialLang, { replace: true });
  rewriteInternalLinksForLang(initialLang);

  // Language toggle handler
  document.addEventListener('click', (e) => {
    const target = e.target instanceof Element ? e.target : null;
    if (!target?.closest('#langBtn')) return;
    e.preventDefault();
    e.stopPropagation();
    const raw = localStorage.getItem('lang') || document.documentElement.lang || 'en';
    const current = String(raw).toLowerCase().startsWith('lo') ? 'lo' : 'en';
    const next = current === 'en' ? 'lo' : 'en';
    localStorage.setItem('lang', next);
    applyLanguage(next);
    setLangUI(next);
    updateUrlLang(next, { replace: true });
    rewriteInternalLinksForLang(next);
  });

  // Sync language on back/forward navigation
  window.addEventListener('popstate', () => {
    const lang = getLangFromUrl() || 'en';
    localStorage.setItem('lang', lang);
    applyLanguage(lang);
    setLangUI(lang);
    rewriteInternalLinksForLang(lang);
  });

  // Project tabs - expose globally
  window.showProjectTab = (type) => showProjectTab(type, document);

  // Attach click handlers for tabs if present
  document.addEventListener('DOMContentLoaded', () => {
    const btnHydro = document.getElementById('btnHydro');
    const btnNon = document.getElementById('btnNonHydro');
    if (btnHydro) btnHydro.addEventListener('click', () => window.showProjectTab('hydropower'));
    if (btnNon) btnNon.addEventListener('click', () => window.showProjectTab('non-hydropower'));
  });

  // Default highlight on pages with tabs
  document.addEventListener('DOMContentLoaded', () => {
    try {
      if (document.getElementById('btnHydro')) window.showProjectTab('hydropower');
    } catch {
      // Silently fail if elements don't exist
    }
  });

  // FAQ accordion - expose globally
  window.toggleFAQ = (faqId) => toggleFAQ(faqId, document);

  // FAQ accordion event delegation
  function handleFaqToggle(e) {
    const btn = e.target?.closest?.('[data-faq-toggle]');
    if (btn) {
      const faqId = btn.getAttribute('data-faq-toggle');
      if (faqId) {
        e.preventDefault();
        window.toggleFAQ(faqId);
      }
    }
  }
  document.addEventListener('click', handleFaqToggle, true);
  document.addEventListener('touchstart', handleFaqToggle, { capture: true, passive: false });

  // Smooth scrolling - expose globally
  window.smoothScrollTo = (target) => smoothScrollTo(target, document);
  window.scrollToTop = () => scrollToTop(window);

  // Add smooth scrolling to anchor links
  document.addEventListener('click', (e) => {
    const target = e.target;
    if (!(target instanceof Element)) return;

    const link = target.closest('a[href^="#"]');
    if (link) {
      e.preventDefault();
      const targetId = link.getAttribute('href');
      if (targetId && targetId !== '#') {
        smoothScrollTo(targetId, document);
      }
    }
  });

  // Scroll to top button functionality
  const scrollToTopBtn = document.querySelector('[data-scroll-to-top]');
  if (scrollToTopBtn) {
    scrollToTopBtn.addEventListener('click', () => scrollToTop(window));
  }

  /**
   * Dynamically populates the clients logo carousel from a manifest JSON file
   */
  async function hydrateClientsCarousel() {
    const track = document.getElementById('clientsTrack');
    if (!track) return;
    try {
      const res = await fetch('/images/clients/manifest.json', { cache: 'no-store' });
      if (!res.ok) return;
      const data = await res.json().catch(() => null);
      const logos = data && Array.isArray(data.logos) ? data.logos : [];
      if (logos.length === 0) return;
      track.innerHTML = '';
      logos.forEach((src) => {
        const item = document.createElement('div');
        item.className = 'logo-item';
        const img = document.createElement('img');
        img.loading = 'lazy';
        img.decoding = 'async';
        img.src = src;
        img.alt = '';
        item.appendChild(img);
        track.appendChild(item);
      });
      const carousel = track.closest('.logo-carousel');
      if (carousel && track.scrollWidth > carousel.clientWidth) {
        carousel.classList.add('scrolling');
      } else if (carousel) {
        carousel.classList.remove('scrolling');
      }
    } catch {
      // silent fail
    }
  }
  hydrateClientsCarousel();

  /**
   * Modern parallax and scroll reveal effects
   */
  function initModernScrollEffects() {
    let ticking = false;

    const revealObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
            revealObserver.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px',
      }
    );

    document.querySelectorAll('.scroll-reveal').forEach((el) => {
      revealObserver.observe(el);
    });

    window.addEventListener(
      'scroll',
      () => {
        if (!ticking) {
          window.requestAnimationFrame(() => {
            updateScrollProgress(window, document);
            ticking = false;
          });
          ticking = true;
        }
      },
      { passive: true }
    );

    updateScrollProgress(window, document);
  }

  if (typeof IntersectionObserver !== 'undefined') {
    initModernScrollEffects();
  }

  // Initialize stagger animations
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => addStaggerAnimation(document));
  } else {
    addStaggerAnimation(document);
  }

  // Analytics (GA4)
  initAnalytics(document, window);

  // Register service worker
  if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
    window.addEventListener('load', () => {
      try {
        navigator.serviceWorker.register('/sw.js').catch(() => {});
      } catch {
        // Ignore service worker registration errors
      }
    });
  }

  // Image protection for elements with .protected-image
  document.addEventListener('contextmenu', (e) => {
    const t = e.target;
    if (t instanceof Element && t.closest('.protected-image')) {
      e.preventDefault();
    }
  });
  document.addEventListener('dragstart', (e) => {
    const t = e.target;
    if (t instanceof Element && t.closest('.protected-image')) {
      e.preventDefault();
    }
  });
})();
